(() => {
  const preferenceWeights = {style: 8, balance: 6, feel: 6, sweetspot: 5, shape: 4};
  const parse = value => {try {return JSON.parse(value);} catch {return null;}};
  const list = value => Array.isArray(value) ? value : value ? [value] : [];
  const unique = values => [...new Set(values)];
  const levels = value => unique(list(value).flatMap(item => {
    const text = String(item).toLowerCase();
    return ['beginner', 'regular', 'advanced'].filter(key => key === 'beginner' ? text.includes('beginner') : key === 'regular' ? text.includes('ervaren') || text.includes('regular') : text.includes('gevorderd') || text.includes('advanced'));
  }));
  const mapValues = (value, names) => unique(list(value).flatMap(item => names[String(item).toLowerCase()] || []));
  const styleNames = {'controle': 'control', 'control': 'control', 'all-round': 'allround', 'allround': 'allround', 'power': 'power'};
  const shapeNames = {'rond': 'round', 'round': 'round', 'druppel': 'teardrop', 'teardrop': 'teardrop', 'diamant': 'diamond', 'diamond': 'diamond'};

  function readCard(card) {
    const profile = parse(card.dataset.pkrgProfile) || {};
    const tags = parse(card.dataset.pkrgTags) || [];
    const customLevels = parse(card.dataset.pkrgLevels);
    const customStyles = parse(card.dataset.pkrgStyles);
    const customShapes = parse(card.dataset.pkrgShapes);
    return {
      card, price: Number(card.dataset.price), id: card.dataset.productId,
      levels: levels(profile.level || (list(customLevels).length ? customLevels : tags)),
      style: mapValues(profile.style || (list(customStyles).length ? customStyles : tags), styleNames),
      shape: mapValues(profile.shape || (list(customShapes).length ? customShapes : tags), shapeNames),
      balance: ['laag', 'midden', 'hoog'].includes(card.dataset.balance) ? [card.dataset.balance] : [],
      feel: ['zacht', 'gemiddeld', 'stevig'].includes(card.dataset.feel) ? [card.dataset.feel] : [],
      sweetspot: ['compact', 'gemiddeld', 'ruim'].includes(card.dataset.sweetspot) ? [card.dataset.sweetspot] : []
    };
  }

  function initialize(root) {
    if (root.dataset.pkrgReady) return;
    const form = root.querySelector('[data-pk-racket-guide]');
    const copy = parse(root.querySelector('[data-pkrg-copy]').textContent);
    if (!form || !copy) return;
    const steps = [...form.querySelectorAll('[data-pkrg-step]')];
    const cards = [...root.querySelectorAll('[data-pkrg-card]')];
    const output = root.querySelector('[data-pkrg-output]');
    const budget = form.elements.budget;
    const currency = new Intl.NumberFormat(document.documentElement.lang || 'nl', {style: 'currency', currency: root.dataset.currency});
    let step = 0;
    const choice = name => form.querySelector(`[name="${name}"]:checked`);
    const label = name => choice(name)?.dataset.label || copy.any;

    function summary() {
      root.querySelectorAll('[data-pkrg-summary]').forEach(element => {
        const key = element.dataset.pkrgSummary;
        element.textContent = key === 'budget' ? currency.format(Number(budget.value) || 0) : key === 'level' && !choice('level') ? copy.unselected : label(key);
      });
      root.querySelectorAll('[data-pkrg-budget-preset]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.pkrgBudgetPreset) === Number(budget.value))));
    }

    function showStep(index, focus = false) {
      step = Math.min(steps.length - 1, Math.max(0, index));
      steps.forEach((fieldset, i) => fieldset.hidden = i !== step);
      root.querySelectorAll('[data-pkrg-step-target]').forEach(button => {
        const active = Number(button.dataset.pkrgStepTarget) === step;
        if (active) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
        button.classList.toggle('is-complete', Number(button.dataset.pkrgStepTarget) < step);
      });
      form.querySelector('[data-pkrg-previous]').hidden = step === 0;
      form.querySelector('[data-pkrg-next]').hidden = step === steps.length - 1;
      form.querySelector('[type=submit]').hidden = step !== steps.length - 1;
      root.querySelector('[data-pkrg-progress]').textContent = copy.step.replace('%current%', step + 1).replace('%total%', steps.length);
      if (focus) {
        steps[step].querySelector('legend').focus({preventScroll: true});
        form.scrollIntoView({block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      }
    }

    function validStep(index) {
      const invalid = [...steps[index].querySelectorAll('input')].find(input => !input.checkValidity());
      if (!invalid) return true;
      showStep(index);
      invalid.reportValidity();
      return false;
    }

    function select() {
      const data = new FormData(form);
      const level = data.get('level');
      const maxPrice = Math.round(Number(data.get('budget')) * 100);
      const compatible = level === 'beginner' ? ['beginner'] : level === 'regular' ? ['regular', 'beginner'] : ['advanced', 'regular'];
      return cards.map(readCard).filter(product => Number.isFinite(product.price) && product.price <= maxPrice && product.levels.some(value => compatible.includes(value))).map(product => {
        const matches = [], unknown = [], differences = [];
        let score = 0;
        if (product.levels.includes(level)) score += 12;
        else differences.push('level');
        for (const [key, weight] of Object.entries(preferenceWeights)) {
          const selected = data.get(key);
          if (!selected || selected === 'any') continue;
          if (!product[key].length) unknown.push(key);
          else if (product[key].includes(selected)) {matches.push(key); score += weight;}
          else {differences.push(key); score -= weight;}
        }
        return {...product, matches, unknown, differences, score, tier: differences.length ? 2 : unknown.length ? 1 : 0};
      }).sort((a, b) => a.tier - b.tier || b.score - a.score || a.price - b.price || a.id.localeCompare(b.id)).slice(0, 3);
    }

    function showResults() {
      const selected = select();
      cards.forEach(card => card.hidden = true);
      const results = root.querySelector('[data-pkrg-results]');
      results.dataset.count = selected.length;
      for (const product of selected) {
        product.card.dataset.matchTier = product.tier;
        product.card.querySelector('[data-pkrg-fit]').textContent = product.tier === 0 ? copy.fullMatch : product.tier === 1 ? copy.compareMatch : copy.alternative;
        const reasons = [product.levels.includes(choice('level').value) ? copy.levelBudget : copy.budget];
        for (const key of product.matches) reasons.push(copy.match.replace('%property%', copy.properties[key]).replace('%value%', label(key)));
        const container = product.card.querySelector('[data-pkrg-reasons]');
        container.replaceChildren(...reasons.slice(0, 4).map(text => {const li = document.createElement('li'); li.textContent = text; return li;}));
        const notes = [];
        if (product.differences.length) notes.push(copy.differs.replace('%properties%', product.differences.map(key => copy.properties[key].toLowerCase()).join(', ')));
        if (product.unknown.length) notes.push(copy.unknown.replace('%properties%', product.unknown.map(key => copy.properties[key].toLowerCase()).join(', ')));
        const attention = product.card.querySelector('[data-pkrg-attention]');
        attention.textContent = notes.join(' ');
        attention.hidden = !notes.length;
        results.append(product.card);
        product.card.hidden = false;
        const image = product.card.querySelector('img');
        if (image) image.loading = 'eager';
      }
      root.querySelector('[data-pkrg-result-count]').textContent = String(selected.length);
      root.querySelector('[data-pkrg-message]').textContent = !selected.length ? copy.empty : selected.every(product => product.tier === 0) ? copy.found : copy.partial;
      results.hidden = !selected.length;
      output.hidden = false;
      output.querySelector('h2').focus({preventScroll: true});
      output.scrollIntoView({block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    }

    form.addEventListener('submit', event => {
      event.preventDefault();
      if (step < steps.length - 1) {if (validStep(step)) showStep(step + 1, true); return;}
      if (!steps.every((_, index) => validStep(index))) return;
      showResults();
    });
    form.addEventListener('input', () => {summary(); output.hidden = true;});
    form.addEventListener('change', () => {summary(); output.hidden = true;});
    root.querySelector('[data-pkrg-next]').addEventListener('click', () => {if (validStep(step)) showStep(step + 1, true);});
    root.querySelector('[data-pkrg-previous]').addEventListener('click', () => showStep(step - 1, true));
    root.querySelectorAll('[data-pkrg-step-target]').forEach(button => button.addEventListener('click', () => {if (Number(button.dataset.pkrgStepTarget) === 0 || validStep(0)) showStep(Number(button.dataset.pkrgStepTarget), true);}));
    root.querySelectorAll('[data-pkrg-budget-preset]').forEach(button => button.addEventListener('click', () => {budget.value = button.dataset.pkrgBudgetPreset; summary(); output.hidden = true;}));
    root.querySelector('[data-pkrg-edit]').addEventListener('click', () => showStep(0, true));
    const preview = new URL(location.href).searchParams.get('preview_theme_id') || (window.Shopify?.theme?.role !== 'main' ? window.Shopify?.theme?.id : null);
    if (preview) root.querySelectorAll('[data-pkrg-product-link]').forEach(link => {const url = new URL(link.href); url.searchParams.set('preview_theme_id', preview); link.href = url;});
    if (matchMedia('(max-width:900px)').matches) root.querySelector('.pkrg-aside details').open = false;
    root.dataset.pkrgReady = 'true';
    root.querySelector('[data-pkrg-navigation]').hidden = false;
    form.querySelector('[type=submit]').disabled = false;
    summary();
    showStep(0);
  }
  const initializeAll = container => container.querySelectorAll('[data-pkrg]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeAll(document));
  else initializeAll(document);
  document.addEventListener('shopify:section:load', event => initializeAll(event.target));
})();
